<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        WordPress Connection
      </h2>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
    </div>

    <!-- Connected state -->
    <div v-else-if="connection" class="space-y-6">
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-3 h-3 rounded-full" :class="connection.status === 'active' ? 'bg-green-500' : 'bg-yellow-500'"></div>
          <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ connection.status === 'active' ? 'Connected' : 'Inactive' }}
          </span>
        </div>

        <dl class="space-y-4">
          <div>
            <dt class="text-xs text-gray-500 dark:text-gray-400">WordPress REST URL</dt>
            <dd class="text-sm text-gray-900 dark:text-gray-100 font-mono mt-1">{{ connection.wp_rest_url }}</dd>
          </div>
          <div>
            <dt class="text-xs text-gray-500 dark:text-gray-400">Plugin Version</dt>
            <dd class="text-sm text-gray-900 dark:text-gray-100 mt-1">{{ connection.plugin_version || 'Unknown' }}</dd>
          </div>
          <div v-if="connection.last_seen_at">
            <dt class="text-xs text-gray-500 dark:text-gray-400">Last Seen</dt>
            <dd class="text-sm text-gray-900 dark:text-gray-100 mt-1">{{ formatDate(connection.last_seen_at) }}</dd>
          </div>
        </dl>

        <div class="mt-6 flex gap-3">
          <button @click="handleTest" :disabled="testing" class="btn btn-secondary btn-sm">
            {{ testing ? 'Testing...' : 'Test Connection' }}
          </button>
          <button @click="handleDisconnect" class="btn btn-secondary btn-sm text-red-600 hover:text-red-700">
            Disconnect
          </button>
        </div>

        <div v-if="testResult !== null" class="mt-4 p-3 rounded-lg text-sm" :class="testResult ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'">
          {{ testResult ? 'Connection is healthy' : 'Connection failed — check that WordPress is running and the GF Pages plugin is active' }}
        </div>
      </div>
    </div>

    <!-- Not connected -->
    <div v-else class="space-y-6">
      <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center">
        <div class="text-gray-400 dark:text-gray-500 mb-4">
          <svg class="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-3.071a4.5 4.5 0 00-6.364-6.364L4.5 8.25l4.5 4.5m6-6l-4.5 4.5" />
          </svg>
        </div>
        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">No WordPress connection</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400 mb-4">
          Connect a WordPress site to publish pages directly from this dashboard.
        </p>

        <form @submit.prevent="handleRegister" class="max-w-md mx-auto text-left">
          <UiInput
            v-model="registerForm.wp_rest_url"
            label="WordPress REST URL"
            placeholder="https://yoursite.com/wp-json"
            required
          />
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Make sure the GF Pages plugin is installed and activated on the WordPress site.
          </p>

          <div v-if="registerError" class="mt-3 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg text-sm">
            {{ registerError }}
          </div>

          <div class="mt-4 flex justify-end">
            <button type="submit" :disabled="registering" class="btn btn-cta btn-sm">
              {{ registering ? 'Connecting...' : 'Connect WordPress Site' }}
            </button>
          </div>
        </form>
      </div>

      <div class="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Setup Instructions</h3>
        <ol class="space-y-2 text-sm text-gray-600 dark:text-gray-400 list-decimal list-inside">
          <li>Install the <strong>GF Pages</strong> plugin on your WordPress site</li>
          <li>Activate the plugin from <strong>Plugins &rarr; Installed Plugins</strong></li>
          <li>Enter your WordPress REST URL above (e.g., <code class="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">https://yoursite.com/wp-json</code>)</li>
          <li>Click <strong>Connect WordPress Site</strong> to generate an API key</li>
          <li>The API key will be automatically registered with the plugin</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getWpConnection, registerWpConnection, testWpConnection, destroyWpConnection } from '~/javascript/api/wp_connections'

const props = defineProps({
  websiteId: { type: String, required: true }
})

const connection = ref(null)
const loading = ref(true)
const testing = ref(false)
const testResult = ref(null)
const registering = ref(false)
const registerError = ref(null)

const registerForm = ref({
  wp_rest_url: ''
})

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  })
}

const fetchConnection = async () => {
  loading.value = true
  try {
    const { data } = await getWpConnection(props.websiteId)
    connection.value = data
  } catch (e) {
    if (e.response?.status === 404) {
      connection.value = null
    }
  } finally {
    loading.value = false
  }
}

const handleTest = async () => {
  testing.value = true
  testResult.value = null
  try {
    const { data } = await testWpConnection(props.websiteId)
    testResult.value = data.healthy
  } catch {
    testResult.value = false
  } finally {
    testing.value = false
  }
}

const handleRegister = async () => {
  registering.value = true
  registerError.value = null
  try {
    const { data } = await registerWpConnection(props.websiteId, {
      wp_connection: registerForm.value
    })
    connection.value = data
  } catch (e) {
    registerError.value = e.response?.data?.message || e.response?.data?.error || 'Failed to connect'
  } finally {
    registering.value = false
  }
}

const handleDisconnect = async () => {
  if (!confirm('Disconnect this WordPress site? Published pages will remain on WordPress but no new pages can be published.')) return
  try {
    await destroyWpConnection(props.websiteId)
    connection.value = null
  } catch (e) {
    console.error('Failed to disconnect', e)
  }
}

onMounted(fetchConnection)
</script>
